import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDeleteNeedDraftMutation } from "../../autogen/bff-api";
import { isNotFoundError } from "../../common/errors/isNotFoundError";
import { useApiError } from "../../common/errors/useApiError";
import { urls } from "../../urls";

export const DeleteNeedDraftButton = ({ needId, refetch }: { needId: string; refetch: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const toast = useToast();
  const confirm = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteDraft] = useDeleteNeedDraftMutation();
  const [isDeleting, setIsDeleting] = useState(false);

  const onConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteDraft({ needId }).unwrap();
      navigate(urls.app.needs.mine);
    } catch (e) {
      if (isNotFoundError(e)) {
        navigate(urls.app.needs.mine);
        return;
      }
      if (isNoLongerADraft(e)) {
        toast({
          status: "warning",
          title: t("The need has already been reported and can't be deleted. Withdraw it instead."),
          isClosable: true,
        });
        refetch();
      } else {
        trigger(e);
      }
      setIsDeleting(false);
      confirm.onClose();
    }
  };

  return (
    <>
      <Button variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />} onClick={confirm.onOpen}>
        {t("Delete draft")}
      </Button>
      <AlertDialog isOpen={confirm.isOpen} leastDestructiveRef={cancelRef} onClose={confirm.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{t("Delete this draft?")}</AlertDialogHeader>
            <AlertDialogBody>
              {t("The draft and everything filled in on it is removed from «My needs». This cannot be undone.")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={confirm.onClose} variant="ghost" isDisabled={isDeleting}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" onClick={onConfirm} ml="3" isLoading={isDeleting}>
                {t("Delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

const isNoLongerADraft = (error: unknown): boolean =>
  (error as { status?: unknown })?.status === 400 && JSON.stringify(error).includes("zz_FieldErrorCode_ImmutableInStatus");
